import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { createRoot } from 'react-dom/client';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useErrorBoundary } from 'react-error-boundary';
import {
  useIsAuthenticated,
  useIsGuest,
  useMsal,
} from 'src/auth/firebaseMsalCompat';
import { GuestProjectGuard } from 'src/auth/GuestProjectGuard';

import { useDialog } from '../components/Dialog';
import { ProjectToolbar } from '../components/project/ProjectToolbar';
import { GuestSaveBanner } from '../components/project/GuestSaveBanner';
import { ScoreCtaBox } from '../components/project/ScoreCtaBox';
import { ScoreSideToggles } from '../components/project/ScoreSideToggles';
import { ProjectMobileBottomBar } from '../components/project/ProjectMobileBottomBar';
import { GenerationWaitingView } from '../components/project/GenerationWaitingView';
import { ScoreViewport } from '../components/project/ScoreViewport';
import { ProjectTutorial } from '../components/project/ProjectTutorial';
import { SinglePartHandDialog } from '../components/project/SinglePartHandDialog';
import { ProjectNotFound } from '../components/project/ProjectNotFound';
import { useGetProjectPoll } from '../hooks/project/useGetProjectPoll';
import {
  ProjectRevisionConflict,
  useProjectActions,
} from '../hooks/project/useProjectActions';

import { useProjectTutorial } from '../hooks/project/useProjectTutorial';
import { useDisplaySettings } from '../hooks/project/useDisplaySettings';
import { FreePlanReminderBar } from '../components/FreePlanReminderBar';
import { useCreditAvailability } from '../hooks/useCreditAvailability';
import { usePdfStateQueue } from '../hooks/usePdfStateQueue';
import type { ScoreSystemData } from '../hooks/usePdfStateQueue';
import { usePdfFingeringInteractions } from '../hooks/usePdfFingeringInteractions';
import { useProjectEditSession } from '../hooks/useProjectEditSession';
import {
  captureFunnelEvent,
  kindFromStorageFileName,
  trackProjectPageAction,
  trackPageView,
  trackButtonClick,
  trackError,
  registerSuperProperties,
  unregisterSuperProperty,
} from '../services/telemetry';
import {
  useProjectFeedbackPrompt,
  type FeedbackTrigger,
} from '../hooks/survey';
import { updatePdfOverlaysWithState } from '../utils/updatePdfOverlaysWithState';
import { exportProjectPdf } from '../utils/pdfExport';
import { downloadProjectXml } from '../utils/downloadProjectXml';
import { guestHomePath } from '../samples/sampleReturnTo';
import { useProjectXmlDownload } from '../hooks/useProjectXmlDownload';
import { useGuestDownloadLock } from '../hooks/useGuestDownloadLock';
import { useIsDesktop } from '../hooks/useIsDesktop';
import { useStickyAboveFooter } from '../hooks/useStickyAboveFooter';
import {
  FingeringOverlay,
  FINGERING_SIZES,
  computeOverlayExpansion,
  fingeringScaleForWidth,
  scaleFingeringConfig,
} from '../components/FingeringOverlay';
import type { FingeringSize } from '../components/FingeringOverlay';

/** Static snapshot that drives this page instead of the project API. */
export interface ProjectPageSampleSource {
  slug: string;
  title: string;
  pageSrcs: string[];
  fingeringData: ScoreSystemData[];
  getPdfPages: () => Promise<string[]>;
  downloadXml: (format: 'standard' | 'sibelius' | 'plain') => Promise<void>;
  footer?: React.ReactNode;
  /** Sample back-pill target. Defaults to `/`. Scanner arrivals go back there. */
  backTo?: string;
}

function resolveScoreSource(
  sample: ProjectPageSampleSource | undefined,
  poll: ReturnType<typeof useGetProjectPoll>
) {
  if (sample) {
    return {
      currentStatus: 'GENERATION_COMPLETED',
      fingering: sample.fingeringData,
      projectName: sample.title,
      error: null as Error | null,
      canDownloadXml: true,
      displaySettings: null,
      hasAnsweredImprovementSurvey: true,
    };
  }
  return {
    currentStatus: poll.currentStatus,
    fingering: poll.fingering,
    projectName: poll.projectName,
    error: poll.error,
    canDownloadXml: Boolean(poll.outputXmlPath),
    displaySettings: poll.displaySettings,
    hasAnsweredImprovementSurvey: poll.hasAnsweredImprovementSurvey,
  };
}

const ProjectPage: React.FC<{ sample?: ProjectPageSampleSource }> = ({
  sample,
}) => {
  const { projectId: routeProjectId } = useParams<{ projectId: string }>();
  const isSample = Boolean(sample);
  const projectId = isSample ? undefined : routeProjectId;
  const sampleProps = useMemo(
    () => (sample ? { slug: sample.slug, name: sample.title } : undefined),
    [sample]
  );
  const navigate = useNavigate();
  const { state: locationState } = useLocation();
  const guestHomeTo = guestHomePath(locationState);
  const pngContainerRef = useRef<HTMLDivElement | null>(null);
  const [isPdfLoaded, setIsPdfLoaded] = useState(false);
  const [overlaysRendered, setOverlaysRendered] = useState(false);
  const [fontPopoverOpen, setFontPopoverOpen] = useState(false);
  const { showBoundary } = useErrorBoundary();
  const isAuthenticated = useIsAuthenticated();
  const isGuest = useIsGuest();
  // Guests get a read-only view: passing the (false-for-guests) auth flag into
  // useProjectActions makes every save a no-op, so edits/toggles work in the
  // browser but never persist.
  const { savePdfProject, loading } = useProjectActions(isAuthenticated);
  const { instance: msalInstance } = useMsal();
  const { showAlert, showConfirm, DialogComponent } = useDialog();
  const handleGuestDownloadLocked = useGuestDownloadLock(showConfirm);
  const lockDownloadsForGuest = isGuest && !isSample;
  const isDesktop = useIsDesktop();

  useEffect(() => {
    registerSuperProperties({
      is_guest: isGuest,
      is_sample: isSample,
      ...(sample ? { sample_slug: sample.slug } : {}),
    });
    return () => {
      unregisterSuperProperty('is_guest');
      unregisterSuperProperty('is_sample');
      unregisterSuperProperty('sample_slug');
    };
  }, [isGuest, isSample, sample]);

  // Samples always use the guest localStorage flag so a sample tour counts
  // as seen on a later guest upload, and so this page never hits member APIs.
  const { hasSeenTutorial, tutorialLoading, markSeen } = useProjectTutorial(
    isSample ? false : isAuthenticated,
    isSample ? true : isGuest
  );
  const [tourOpen, setTourOpen] = useState(false);
  const autoLaunchedTourRef = useRef(false);

  const poll = useGetProjectPoll({
    projectId: projectId ?? '',
  });

  const {
    statusMessage,
    isPolling,
    answeredWaitingSurveyTypes,
    storageFileName,
    reportedInstrument,
  } = poll;

  const {
    currentStatus,
    fingering,
    projectName,
    error,
    canDownloadXml,
    displaySettings,
    hasAnsweredImprovementSurvey,
  } = resolveScoreSource(sample, poll);

  const pageSrcs = useMemo(() => {
    if (sample) return sample.pageSrcs;
    return Array.isArray(poll.fileXML)
      ? poll.fileXML.map((pngBlob) => `data:image/png;base64,${pngBlob}`)
      : [];
  }, [sample, poll.fileXML]);

  // Per-project: this score IS truncated. Samples always are. Live projects
  // count as demo only when generation recorded zero credits spent;
  // null/absent (legacy) is treated as paid so we never watermark a paid
  // score. Drives the download watermark / attribution line only.
  const isDemoGeneration = sample ? true : poll.creditConsumed === 0;
  // Per-account: can they generate a full score right now? Drives only the
  // reminder CTA. Guests and samples skip the billing read entirely.
  const { hasSpendableCredits, entitlement } = useCreditAvailability(
    isAuthenticated && !isSample && !isGuest
  );
  // Guests already have GuestSaveBanner (sign up first); the upgrade
  // reminder is for signed-in free users only. Wait for entitlement so
  // we never flash "Upgrade" at a paying customer.
  const showDemoReminder =
    !isSample &&
    !isGuest &&
    isDemoGeneration &&
    currentStatus === 'GENERATION_COMPLETED' &&
    entitlement === 'known';
  const demoReminderMode = hasSpendableCredits ? 'purchased' : 'upsell';

  // Generation hasn't started: the score has one staff and we're waiting on
  // the user to tell us which hand it is. Unlike the XML page, nothing about
  // rendering depends on the answer here — the OMR pipeline bakes the label
  // side into each fingering group's `upward` flag.
  const awaitingHandSelection = currentStatus === 'AWAITING_HAND_SELECTION';

  // Persisted view toggles (alt fingerings, accidental, pitch). The
  // accidental + pitch overlays render on top of the existing fingering
  // pills using the per-system `noteheadDataList` emitted by the GPU
  // server, and remain hidden on legacy projects without that data (the
  // side-toggles are also hidden via `hasNoteheadData` below). Pitch
  // defaults OFF; the others default ON. Hydrated from the server once
  // generation has completed, then debounce-saved on every flip.
  const {
    showAltFingerings,
    showAccidental,
    showPitch,
    showFingerings,
    fontSize: fingeringSize,
    setShowAltFingerings,
    setShowAccidental,
    setShowPitch,
    setShowFingerings,
    setFontSize,
  } = useDisplaySettings({
    projectId,
    serverSettings: displaySettings,
    ready: currentStatus === 'GENERATION_COMPLETED',
    persist: !isSample && isAuthenticated,
    displayRevision: poll.displayRevision,
    onDisplayRevision: poll.setDisplayRevision,
    onSaveError: (message) => {
      void showAlert('Settings not saved', message);
    },
  });

  const armFeedbackRef = useRef<
    (trigger: Exclude<FeedbackTrigger, 'manual'>) => void
  >(() => {});

  // Use ref instead of state to avoid re-rendering all overlays on edit
  const currentFingeringRef = useRef<ScoreSystemData[] | null>(null);

  // Tracks the projectId we've already reported ``project_rendered_successfully``
  // for, so subsequent fingering-size changes / poll refreshes don't re-fire.
  const reportedRenderedProjectIdRef = useRef<string | null>(null);

  // Mirror showAccidental / showPitch into refs so the per-image
  // ResizeObserver callback set up inside the main rebuild effect can
  // read the LATEST toggle values when it re-renders an overlay.
  // Without these, the ResizeObserver closure would capture whatever
  // value of showAccidental/showPitch existed at the moment the effect
  // ran (typically the defaults), and a layout reflow afterwards
  // would silently reset the visibility of the SVG groups.
  const showAccidentalRef = useRef(showAccidental);
  const showPitchRef = useRef(showPitch);
  const showFingeringsRef = useRef(showFingerings);
  const showAltFingeringsRef = useRef(showAltFingerings);
  // One re-render per system overlay, set by the rebuild effect. The
  // fingering toggles call them so each row's padding fits what shows.
  const overlayRenderersRef = useRef<Array<() => void>>([]);
  useEffect(() => {
    showAccidentalRef.current = showAccidental;
  }, [showAccidental]);
  useEffect(() => {
    showPitchRef.current = showPitch;
  }, [showPitch]);
  useEffect(() => {
    showFingeringsRef.current = showFingerings;
  }, [showFingerings]);
  useEffect(() => {
    showAltFingeringsRef.current = showAltFingerings;
  }, [showAltFingerings]);

  // Callback to update overlays after undo/redo
  const updateDisplay = useCallback((newState: ScoreSystemData[]) => {
    // Update the ref with new state
    currentFingeringRef.current = newState;
    // Update all SVG overlays with the new state
    updatePdfOverlaysWithState(newState);
  }, []);

  const { currentStateIndex, setLastSavedStateIndex, addToStateQueue } =
    usePdfStateQueue(updateDisplay);

  // Redirect if no projectId. Samples legitimately have none.
  useEffect(() => {
    if (!isSample && !projectId) {
      navigate('/');
      return;
    }
  }, [isSample, projectId, navigate]);

  // Handle errors
  useEffect(() => {
    if (error) {
      showBoundary(error);
    }
  }, [error, showBoundary]);

  useEffect(() => {
    if (isSample) {
      trackPageView('Sample Project', '/sample/<slug>', {
        slug: sample?.slug,
        name: sample?.title,
      });
      return;
    }
    document.title = 'Project - PineappleMusicLab';
    trackPageView('Project', `/pdf-project/<id>`, { projectId: projectId });
  }, [isSample, projectId, sample?.slug, sample?.title]);

  // Sync fingering data from hook to ref (doesn't trigger re-renders)
  useEffect(() => {
    if (fingering && Array.isArray(fingering)) {
      currentFingeringRef.current = fingering as unknown as ScoreSystemData[];
    }
  }, [fingering]);

  // Save without an automatic state-queue update — used both by the
  // explicit Save Changes button (gone in the new UI) and by the
  // edit-mode Done flow below. Returns a boolean so the Done handler
  // can stay in edit mode if the save fails.
  //
  // The `source` argument lets the telemetry distinguish auto-saves
  // triggered by the edit-mode Done button from any future manual
  // save flow. The `Save Changes` event is fired AFTER the request
  // resolves so it carries a `success` flag — the previous shape
  // fired before the await and so couldn't tell success from failure.
  const handleSaveChanges = useCallback(
    async (source: 'edit-done' | 'manual' = 'manual'): Promise<boolean> => {
      if (isSample) return true;
      if (!projectId || !currentFingeringRef.current) return false;

      try {
        const saved = await savePdfProject(
          projectId,
          currentFingeringRef.current,
          poll.revision
        );
        if (saved && typeof saved.revision === 'number') {
          poll.setRevision(saved.revision);
        }
        setLastSavedStateIndex(currentStateIndex);
        armFeedbackRef.current('edit-commit');
        trackButtonClick('PdfProjectPage', 'Save Changes', {
          source,
          success: true,
          projectId,
        });
        trackProjectPageAction('save_changes', 'pdf', { source, projectId });
        return true;
      } catch (err) {
        if (err instanceof ProjectRevisionConflict) {
          await showAlert(
            'Score changed',
            'This project was updated somewhere else. Reload the page before saving again.'
          );
          return false;
        }
        console.error('Failed to save changes:', err);
        const errorObj = err instanceof Error ? err : new Error(String(err));
        trackError(errorObj, {
          source: 'PdfProjectPage',
          action: 'save',
          projectId,
        });
        trackButtonClick('PdfProjectPage', 'Save Changes', {
          source,
          success: false,
          projectId,
        });
        await showAlert(
          'Save Failed',
          'Could not save your fingering changes. Please try again.'
        );
        return false;
      }
    },
    [
      isSample,
      projectId,
      savePdfProject,
      setLastSavedStateIndex,
      currentStateIndex,
      poll.revision,
      poll.setRevision,
      showAlert,
    ]
  );

  // ─── Edit-mode handlers (mirror Mobile WebView semantics) ─────────
  // Snapshot type is `ScoreSystemData[]`. Apply restores both the
  // ref + DOM overlays AND pushes the restored state into the
  // session state-queue so the queue's notion of "current state"
  // doesn't drift from what's painted.
  const {
    isEditMode,
    isEditModeRef,
    editUndoCount,
    pushEditUndo,
    editUndo: handleEditUndo,
    editReset: handleEditReset,
    editDone: handleEditDone,
    togglePencil: togglePencilFromHook,
  } = useProjectEditSession<ScoreSystemData[]>({
    getSnapshot: () => currentFingeringRef.current,
    applySnapshot: (snapshot) => {
      updateDisplay(snapshot);
      addToStateQueue(snapshot);
    },
    save: handleSaveChanges,
    telemetrySource: 'PdfProjectPage',
  });

  const {
    feedbackOpen,
    onFeedbackOpenChange,
    markFeedbackSubmitted,
    armTrigger,
    activeTrigger,
  } = useProjectFeedbackPrompt({
    ready: isPdfLoaded,
    enabled: Boolean(projectId) && !isSample,
    projectId,
    pageType: 'pdf',
    hasAnsweredImprovementSurvey,
    isEditMode,
    blocked: tourOpen || tutorialLoading || hasSeenTutorial !== true,
  });
  armFeedbackRef.current = armTrigger;

  const feedback = projectId
    ? {
        projectId,
        open: feedbackOpen,
        onOpenChange: onFeedbackOpenChange,
        onSubmitted: markFeedbackSubmitted,
        trigger: activeTrigger,
        pageType: 'pdf' as const,
        onManualOpen: () => trackButtonClick('PdfProjectPage', 'Open Feedback'),
      }
    : undefined;

  const handleTogglePencil = useCallback(async () => {
    await togglePencilFromHook();
  }, [togglePencilFromHook]);

  const handleToggleAltFingerings = useCallback(async () => {
    if (!showFingerings) return;
    // Mobile parity: turning alt fingerings OFF mid-edit auto-commits
    // the in-progress edits before flipping the toggle. See
    // `useProjectEditState.handleFingeringToggle` lines 38-48.
    if (isEditModeRef.current && showAltFingerings) {
      await handleEditDone();
    }
    setShowAltFingerings((prev) => !prev);
    trackButtonClick('PdfProjectPage', 'Toggle Alternative Fingerings');
    trackProjectPageAction('alt_fingerings', 'pdf', {
      state: !showAltFingerings ? 'on' : 'off',
      projectId,
    });
  }, [
    showFingerings,
    showAltFingerings,
    handleEditDone,
    isEditModeRef,
    projectId,
  ]);

  const handleToggleFingerings = useCallback(async () => {
    if (isEditModeRef.current && showFingerings) {
      await handleEditDone();
    }
    setShowFingerings((prev) => !prev);
    trackButtonClick('PdfProjectPage', 'Toggle Fingerings');
    trackProjectPageAction('fingerings', 'pdf', {
      state: !showFingerings ? 'on' : 'off',
      projectId,
    });
  }, [showFingerings, handleEditDone, isEditModeRef, projectId]);

  // Discriminator for the new pitch/accidental overlay toggles. The two
  // toolbar buttons only render when at least one system carries a
  // non-empty `noteheadDataList`; this keeps legacy projects (which
  // come back through the existing FE types as `undefined` here) from
  // showing broken toggles. Cast through the local FE type set since
  // `useGetProjectPoll` returns the union with `XmlProjectFingeringData`.
  const hasNoteheadData = useMemo(() => {
    if (!fingering || !Array.isArray(fingering)) return false;
    return (fingering as unknown as ScoreSystemData[]).some(
      (sys) =>
        Array.isArray(sys?.noteheadDataList) && sys.noteheadDataList.length > 0
    );
  }, [fingering]);

  const handleToggleAccidental = useCallback(() => {
    setShowAccidental((prev) => !prev);
    trackButtonClick('PdfProjectPage', 'Toggle Accidental Overlay');
    trackProjectPageAction('accidental', 'pdf', {
      state: !showAccidental ? 'on' : 'off',
      projectId,
    });
  }, [projectId, showAccidental]);

  const handleTogglePitch = useCallback(() => {
    setShowPitch((prev) => !prev);
    trackButtonClick('PdfProjectPage', 'Toggle Pitch Overlay');
    trackProjectPageAction('pitch', 'pdf', {
      state: !showPitch ? 'on' : 'off',
      projectId,
    });
  }, [projectId, showPitch]);

  const [isExportingPdf, setIsExportingPdf] = useState(false);
  // Toolbar download dropdown (PDF / XML) — controlled here so the
  // PDF / XML handlers below can also close the menu via the parent.
  const [downloadPopoverOpen, setDownloadPopoverOpen] = useState(false);

  const handleDownloadPdf = async () => {
    // Replaces the legacy browser-print flow. We now draw a vector
    // PDF directly with jspdf, so digits and pills land at the
    // exact coordinates computed against the rendered system
    // images — no print-engine layout drift, no @media print
    // transform hacks.
    trackButtonClick('PdfProjectPage', 'Download PDF', sampleProps);
    trackProjectPageAction('download_pdf', 'pdf', {
      ...(projectId ? { projectId } : (sampleProps ?? {})),
      watermarked: isDemoGeneration,
    });

    if (pageSrcs.length === 0 || !currentFingeringRef.current) {
      await showAlert(
        'Not ready',
        'Please wait until the sheet music has finished loading before downloading.'
      );
      return;
    }

    setIsExportingPdf(true);
    try {
      // The exporter reads PNG dimensions out of the raw bytes, so a sample
      // has to resolve its bundled images to base64 first. Live projects
      // already hold the bytes from the poll.
      const pages = sample
        ? await sample.getPdfPages()
        : (poll.fileXML as string[]);

      await exportProjectPdf({
        pages,
        fingeringData: currentFingeringRef.current,
        showAltFingerings,
        showFingerings,
        showAccidental,
        showPitch,
        fingeringSize,
        projectName,
        attribution: isDemoGeneration,
        projectId,
      });
      armFeedbackRef.current('download');
    } catch (err) {
      console.error('Failed to export PDF:', err);
      const errorObj = err instanceof Error ? err : new Error(String(err));
      trackError(errorObj, {
        source: 'PdfProjectPage',
        action: 'downloadPdf',
        ...(projectId ? { projectId } : (sampleProps ?? {})),
      });
      await showAlert(
        'Download Failed',
        'Could not generate the PDF. Please try again.'
      );
    } finally {
      setIsExportingPdf(false);
    }
  };

  // Server-rendered MusicXML download. Only enabled when the project
  // has finished XML synthesis (`outputXmlPath` set by the GPU
  // pipeline); the toolbar dropdown hides the option entirely while
  // this is undefined, so users on legacy / mid-flight projects don't
  // see a button that would 404.
  //
  // The Sibelius variant goes through the same util + endpoint with
  // `format: 'sibelius'`. Busy flags, telemetry, and error-toast copy
  // live in `useProjectXmlDownload`; this page just describes how to
  // fetch the bytes.
  const downloadXmlBytes = useCallback(
    async (format: 'standard' | 'sibelius' | 'plain') => {
      if (sample) {
        await sample.downloadXml(format);
        return;
      }
      if (!projectId) {
        throw new Error('Cannot download MusicXML without a project id');
      }
      await downloadProjectXml(projectId, msalInstance, { format });
    },
    [sample, projectId, msalInstance]
  );

  const {
    handleDownloadXml,
    handleDownloadXmlSibelius,
    handleDownloadXmlPlain,
    isDownloadingXml,
    isDownloadingXmlSibelius,
    isDownloadingXmlPlain,
  } = useProjectXmlDownload({
    source: 'PdfProjectPage',
    projectId,
    downloader: downloadXmlBytes,
    showAlert,
    onDownloadSuccess: () => armFeedbackRef.current('download'),
    extras: sampleProps,
  });

  // Drag / long-press / swap on the SVG overlay. Delegated on
  // `pngContainerRef` so overlay rebuilds cannot drop the handlers.
  usePdfFingeringInteractions({
    currentFingeringRef,
    addToStateQueue,
    isEditModeRef,
    pushEditUndo,
    rootRef: pngContainerRef,
  });

  // Track current render cycle to prevent stale callbacks from setting overlaysRendered
  const renderIdRef = useRef(0);

  useEffect(() => {
    // Display the score images once their sources are available.

    // Track all ResizeObservers created during this run so we can
    // disconnect them when the effect cleans up (deps change /
    // unmount). Each per-image observer re-renders its overlay on
    // layout changes — see the inline comment where it's created
    // for the bug it fixes. Declared at the effect level (not
    // inside the `if`) so the cleanup return below can reach it.
    const resizeObservers: ResizeObserver[] = [];
    // Track every React 18 root we mount for the per-image overlays
    // so the cleanup below can `unmount()` them before the next run
    // wipes `pngContainerRef.innerHTML`. Without this, the roots
    // leak (they keep their internal fibers + state but their
    // containers are detached from the DOM), which trips
    // React 18's "synchronously unmount a root that was unmounted
    // from a different render" warnings on hot reloads / re-renders.
    const overlayRoots: Array<ReturnType<typeof createRoot>> = [];

    if (
      pageSrcs.length > 0 &&
      pngContainerRef.current &&
      currentFingeringRef.current
    ) {
      // Increment render ID to invalidate any pending callbacks from previous renders
      renderIdRef.current += 1;
      const currentRenderId = renderIdRef.current;

      // Reset overlays rendered flag when loading new content
      setOverlaysRendered(false);

      pngContainerRef.current.innerHTML = '';
      overlayRenderersRef.current = [];

      let loadedImagesCount = 0;
      const totalImages = pageSrcs.length;

      pageSrcs.forEach((pageSrc, index) => {
        // Create container for image and overlay
        const container = document.createElement('div');
        container.style.position = 'relative';
        container.style.display = 'inline-block';
        container.style.marginBottom = '16px';
        container.style.width = '100%';
        // `isolation: isolate` forces this container to be the
        // bounding stacking context for the `mix-blend-mode: lighten`
        // accidental rects rendered inside the SVG overlay. Without
        // isolation, the rect's blend has no well-defined backdrop
        // (the SVG and `overlayContainer` each create their own
        // non-isolated stacking contexts via `z-index`, and the
        // browser falls back to compositing the rect against
        // transparent — which looks like a solid colored bbox
        // rather than a tint over the engraved notehead). With
        // isolation here, both the <img> drawn first and the
        // overlay SVG drawn second share this stacking context, so
        // the rect's `lighten` blend uses the rasterized notehead /
        // staff lines as its source backdrop.
        container.style.isolation = 'isolate';

        // Create image element
        const img = document.createElement('img');
        img.src = pageSrc;
        img.alt = `${projectName || 'Sheet music'}, system ${index + 1}`;
        img.style.width = '100%';
        img.style.height = 'auto';
        img.style.display = 'block';

        // Wait for image to load to get dimensions
        img.onload = () => {
          // Check if this callback is from a stale render cycle
          if (currentRenderId !== renderIdRef.current) {
            return;
          }

          // Add image to container first
          container.appendChild(img);

          // Use requestAnimationFrame to ensure DOM layout is complete
          requestAnimationFrame(() => {
            // Check again after requestAnimationFrame in case fingeringSize changed
            if (currentRenderId !== renderIdRef.current) {
              return;
            }

            const imageWidth = img.naturalWidth;
            const imageHeight = img.naturalHeight;
            let displayWidth = img.offsetWidth;
            let displayHeight = img.offsetHeight;

            // Fallback: if display dimensions are 0, use natural dimensions
            if (displayWidth === 0 || displayHeight === 0) {
              displayWidth = imageWidth;
              displayHeight = imageHeight;
            }

            // Get current size configuration (unscaled tier). The
            // effective config is derived per-render below so the digit
            // size tracks the displayed image width.
            const sizeConfig = FINGERING_SIZES[fingeringSize];

            // Create React root for SVG overlay
            const overlayContainer = document.createElement('div');
            overlayContainer.id = `fingering-overlay-${index}`;
            overlayContainer.style.position = 'absolute';
            overlayContainer.style.top = '0px';
            overlayContainer.style.left = '0px';
            overlayContainer.style.width = '100%';
            overlayContainer.style.height = '100%';
            overlayContainer.style.pointerEvents = 'auto'; // Enable pointer events for interaction
            // INTENTIONALLY no z-index: adding one would create a
            // stacking context that breaks `mix-blend-mode: lighten`
            // on the accidental rect divs rendered inside (Chrome's
            // compositor doesn't reliably pass mix-blend-mode
            // through nested non-isolated stacking contexts even
            // when the CSS spec says it should). overlayContainer
            // still paints on top of the static <img> sibling via
            // normal document order because it's `position:
            // absolute`. The SVG inside still has its own z-index
            // 10 so it paints above the accidental wrapper.
            container.appendChild(overlayContainer);

            // Render SVG overlay using React. We re-render through
            // this helper on every img-resize event below, so the
            // FingeringOverlay always sees the LATEST displayWidth /
            // displayHeight as the browser actually paints them.
            const root = createRoot(overlayContainer);
            overlayRoots.push(root);
            const renderOverlay = () => {
              // Measure on every call rather than reusing the closure
              // values from the rAF callback. The first measurement
              // can land before the layout fully settles (more imgs
              // loading, scrollbar appearing/disappearing, window
              // resize), so closure values are stale by the time the
              // user actually sees the page. Falling back to
              // imageWidth/Height keeps us safe if the img is briefly
              // hidden / detached.
              const w = img.offsetWidth || imageWidth;
              const h = img.offsetHeight || imageHeight;

              // Read the data on every call too: undo/redo swaps in a
              // copy, and a toggle re-render must not draw the old one.
              // `noteheadDataList` feeds the pitch/accidental overlays
              // and is missing on legacy projects.
              const system = currentFingeringRef.current?.[index];
              const fingeringGroups = system?.fingeringGroupList ?? [];
              const noteheadDataList = system?.noteheadDataList;

              // Shrink the digits (and their pills) with the displayed
              // image: below the reference width the whole tier scales
              // down linearly, so the fingering size stays proportional
              // to the score on phones/tablets.
              const scaledConfig = scaleFingeringConfig(
                sizeConfig,
                fingeringScaleForWidth(w)
              );

              // Container padding MUST be recomputed from the SAME
              // (scaled) config the overlay renders with — the SVG's
              // `coordinateOffsetY` / `totalHeight` derive from it, and
              // any mismatch reintroduces the vertical-scale drift. So
              // we set it here, on every (re)render, rather than once.
              const { maxExpansionUp, maxExpansionDown } =
                computeOverlayExpansion(fingeringGroups, scaledConfig, {
                  scaleY: h / imageHeight,
                  displayHeight: h,
                  showFingerings: showFingeringsRef.current,
                  showAltFingerings: showAltFingeringsRef.current,
                });
              container.style.paddingTop = `${maxExpansionUp}px`;
              container.style.paddingBottom = `${maxExpansionDown}px`;
              container.style.boxSizing = 'border-box';

              root.render(
                <FingeringOverlay
                  fingeringGroups={fingeringGroups}
                  noteheadDataList={noteheadDataList}
                  imageWidth={imageWidth}
                  imageHeight={imageHeight}
                  displayWidth={w}
                  displayHeight={h}
                  imageIndex={index}
                  sizeConfig={scaledConfig}
                  showAccidental={showAccidentalRef.current}
                  showPitch={showPitchRef.current}
                  showFingerings={showFingeringsRef.current}
                  showAltFingerings={showAltFingeringsRef.current}
                />
              );
            };
            overlayRenderersRef.current[index] = renderOverlay;
            renderOverlay();

            // Watch the img for size changes and re-render the
            // overlay against the new dimensions. The SVG inside
            // FingeringOverlay uses `viewBox` + `preserveAspectRatio=
            // "none"` + `width/height=100%`, so it visually stretches
            // with its parent container — but the bbox positions are
            // computed against `scaleY = displayHeight / imageHeight`
            // at render time, and if the actual rendered height drifts
            // after that initial measurement (a later img triggers a
            // scrollbar, a window resize re-flows the row), the bbox
            // math no longer matches the stretched viewBox. The visible
            // symptom is bboxes / pitch letters that "fan out" from
            // some midpoint inside a row — items near the top of a row
            // sit too high, items near the bottom sit too low (or
            // vice versa, depending on the direction of the drift).
            // Re-rendering on resize keeps scaleY honest.
            const ro = new ResizeObserver(() => {
              if (currentRenderId !== renderIdRef.current) return;
              renderOverlay();
            });
            ro.observe(img);
            resizeObservers.push(ro);

            // Increment loaded images counter
            loadedImagesCount++;

            // Track successful PDF result loading only when all images are loaded
            if (loadedImagesCount === totalImages) {
              // Signal that all overlays have been rendered
              // This will trigger the useEffect to attach event handlers
              setOverlaysRendered(true);
            }
          });
        };

        pngContainerRef.current?.appendChild(container);
      });
    }
    return () => {
      // Disconnect every per-image ResizeObserver attached during
      // this run before the next run (or unmount) replaces them.
      // Otherwise observers would keep firing on the now-detached
      // <img> elements (the innerHTML='' at the top of the next run
      // removes them from the DOM but doesn't cancel the observers).
      resizeObservers.forEach((ro) => ro.disconnect());
      // These roots are about to unmount; a toggle must not render them.
      overlayRenderersRef.current = [];
      // Unmount each overlay root BEFORE the next run wipes
      // `pngContainerRef.innerHTML`. Skipping this leaks React 18
      // roots: their fibers stay alive in memory, their containers
      // get detached, and any state inside FingeringOverlay (effects,
      // observers) keeps running against the orphan DOM. We defer
      // with `queueMicrotask` because React forbids unmounting a
      // root synchronously from inside a render phase — effect
      // cleanups can run during a re-render in concurrent mode.
      overlayRoots.forEach((root) => {
        queueMicrotask(() => root.unmount());
      });
    };
    // Note: currentFingeringRef is a ref, not included in dependencies
    // This prevents re-rendering when fingering data is edited
    // showAltFingerings is also not included to prevent full overlay recreation
    // fingeringSize is included to re-render overlays when size changes
    // showAccidental / showPitch are deliberately routed through refs
    // (showAccidentalRef / showPitchRef) so the ResizeObserver
    // re-render callback can read the LATEST values without forcing
    // this effect to re-run on every toggle. A toggle-driven re-run
    // would do `pngContainerRef.innerHTML = ''` and re-measure
    // `img.offsetWidth` / `offsetHeight` against the freshly-emptied
    // container; the transient empty state can shift the scrollbar
    // and the parent width, which changes the measured image height
    // and visibly alters the spacing between system rows on every
    // toggle. The sibling useEffect below mutates `display` directly
    // on the existing `<g>` groups instead.
  }, [pageSrcs, fingeringSize, projectName]);

  // Toggle visibility of the accidental + pitch overlays in-place
  // without rebuilding the image stack. FingeringOverlay always
  // renders the accidental overlay (HTML <div>, mix-blend-mode tint)
  // and the pitch overlay (SVG <g>) when noteheadDataList exists;
  // this effect just flips their `display` on toggle change. See
  // the comment block on the main overlay-creation effect above for
  // why we don't add showAccidental/showPitch to its deps.
  //
  // Note: the accidental overlay is a sibling of the <svg> (an HTML
  // <div>), not a `<g>` inside it — SVG children can't blend with
  // the underlying <img> via mix-blend-mode. So the accidental
  // selector here scopes to the whole overlayContainer (which
  // contains both the HTML accidental wrapper and the <svg>), while
  // the pitch selector still drills into the SVG.
  useEffect(() => {
    pageSrcs.forEach((_, index) => {
      const overlayContainer = document.getElementById(
        `fingering-overlay-${index}`
      );
      if (!overlayContainer) return;

      const accidentalWrapper = overlayContainer.querySelector<HTMLElement>(
        'div[data-overlay-type="accidental"]'
      );
      if (accidentalWrapper) {
        accidentalWrapper.style.display = showAccidental ? 'block' : 'none';
      }

      const svg = overlayContainer.querySelector('svg');
      if (!svg) return;

      const pitchGroup = svg.querySelector<SVGGElement>(
        'g[data-overlay-type="pitch"]'
      );
      if (pitchGroup) {
        pitchGroup.style.display = showPitch ? 'block' : 'none';
      }
    });
  }, [showAccidental, showPitch, pageSrcs, overlaysRendered]);

  // After overlays paint: seed the undo queue and unlock surveys.
  // Edit listeners live on `pngContainerRef` (delegated) so a later
  // overlay rebuild does not need to re-bind them.
  useEffect(() => {
    if (!overlaysRendered) return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    addToStateQueue(currentFingeringRef.current as any);
    setLastSavedStateIndex(0);
    setIsPdfLoaded(true);
  }, [overlaysRendered, addToStateQueue, setLastSavedStateIndex]);

  // Once per project, after overlays + storage path are ready. Waits for
  // ``storageFileName`` so image OMR is not mis-tagged as ``pdf``.
  useEffect(() => {
    if (!overlaysRendered || !projectId || !storageFileName) return;
    if (reportedRenderedProjectIdRef.current === projectId) return;
    reportedRenderedProjectIdRef.current = projectId;
    trackButtonClick('PdfProjectPage', 'PDF Result Loaded Successfully');
    captureFunnelEvent('project_rendered_successfully', {
      type: 'pdf',
      kind: kindFromStorageFileName(storageFileName),
      projectId,
    });
  }, [overlaysRendered, projectId, storageFileName]);

  // Fingering toggles re-render every system overlay. The overlay sets
  // digit visibility from the toggles, and each row's padding is resized
  // to the digits that now show. A rebuild reads the same toggles through
  // refs, so it needs no re-run here.
  useEffect(() => {
    overlayRenderersRef.current.forEach((render) => render?.());
  }, [showAltFingerings, showFingerings]);

  const handleFontSizeChange = useCallback(
    (next: FingeringSize) => {
      setFontSize(next);
      // Single event name + size property keeps cardinality bounded and
      // makes "total font-size changes" a one-query aggregate in App
      // Insights (vs. three distinct event names previously).
      trackButtonClick('PdfProjectPage', 'Change Fingering Size', {
        size: next,
        ...sampleProps,
      });
      trackProjectPageAction('fingering_size', 'pdf', {
        size: next,
        ...(projectId ? { projectId } : (sampleProps ?? {})),
      });
    },
    [projectId, sampleProps, setFontSize]
  );

  const isReady =
    currentStatus === 'GENERATION_COMPLETED' && pageSrcs.length > 0;

  const bottomBarHeight = useStickyAboveFooter({
    enabled: isReady && !isDesktop,
    selector: '[data-mobile-bottom-bar]',
  });
  const mobileBottomPad = isDesktop ? 0 : bottomBarHeight || 64;

  // Auto-launch the onboarding tour once, the first time a user reaches a
  // ready project page. Guarded by a ref so a poll refresh or font-size
  // change can't re-fire it within the session; `markSeen` (called on
  // close) persists the flag so it won't re-show on later visits.
  useEffect(() => {
    if (
      isReady &&
      !tutorialLoading &&
      hasSeenTutorial === false &&
      !autoLaunchedTourRef.current
    ) {
      autoLaunchedTourRef.current = true;
      setTourOpen(true);
      trackButtonClick('PdfProjectPage', 'Auto-Launch Tutorial', sampleProps);
    }
  }, [isReady, tutorialLoading, hasSeenTutorial, sampleProps]);

  const handleTourClose = useCallback(() => {
    setTourOpen(false);
    // Idempotent on the backend, so it's safe to call on every close
    // (first-run finish/skip and "?"-triggered replays alike).
    markSeen();
  }, [markSeen]);

  const scoreFooter =
    isReady &&
    (sample?.footer || (isGuest && !isSample) || showDemoReminder) ? (
      <ScoreCtaBox>
        {sample?.footer ??
          (isGuest && !isSample ? (
            <GuestSaveBanner variant="pdf" />
          ) : (
            <FreePlanReminderBar variant="pdf" mode={demoReminderMode} />
          ))}
      </ScoreCtaBox>
    ) : undefined;

  if (projectId && poll.notFound) {
    return <ProjectNotFound projectId={projectId} type="pdf" />;
  }

  return (
    // The viewport-wide neutral grey (#E6E7E7) sits BEHIND the
    // centred max-w-7xl score column, so the page reads as a sheet
    // of paper floating on a soft grey desktop. The toolbar keeps
    // its own white bg so it still pops against this grey when
    // pinned at the top. While waiting there is no sheet, so the page
    // uses the workspace white instead.
    <div
      className={`
        flex min-h-[calc(100vh-4rem)] min-w-0 flex-col overflow-x-clip
        ${
          currentStatus === 'GENERATION_COMPLETED'
            ? 'bg-[#E6E7E7]'
            : 'bg-[#fbfcfc]'
        }
      `}
      style={
        mobileBottomPad > 0 ? { paddingBottom: mobileBottomPad } : undefined
      }
    >
      <div className="min-w-0 flex-grow font-[Outfit]">
        {/* Toolbar lives OUTSIDE the centered max-w-7xl column so its
            white background spans the full viewport width — without
            this, on viewports wider than 7xl the toolbar would have
            #E6E7E7 strips on either side. The toolbar's own inner
            row caps content at max-w-7xl. */}
        {isReady && (
          <ProjectToolbar
            mode={isEditMode ? 'edit' : 'normal'}
            projectName={projectName}
            backTo={
              isSample
                ? (sample?.backTo ?? '/')
                : isGuest
                  ? guestHomeTo
                  : '/workspace'
            }
            backLabel={isSample || isGuest ? 'Home' : 'My Project'}
            onBack={() => {
              if (isSample) {
                trackButtonClick(
                  'PdfProjectPage',
                  'Back to Home (Sample)',
                  sampleProps
                );
                return;
              }
              trackButtonClick(
                'PdfProjectPage',
                isGuest ? 'Back to Home' : 'Back to Workspace'
              );
            }}
            fontPopoverOpen={fontPopoverOpen}
            onFontPopoverOpenChange={setFontPopoverOpen}
            fontSize={fingeringSize}
            onFontSizeChange={handleFontSizeChange}
            downloadPopoverOpen={downloadPopoverOpen}
            onDownloadPopoverOpenChange={setDownloadPopoverOpen}
            // Guest downloads are locked except on samples, where the
            // download is the thing we want people to try.
            onDownloadPdf={
              lockDownloadsForGuest
                ? handleGuestDownloadLocked
                : handleDownloadPdf
            }
            isDownloadingPdf={isExportingPdf}
            xmlAvailability={canDownloadXml ? 'ready' : 'unavailable'}
            onDownloadXml={
              lockDownloadsForGuest
                ? handleGuestDownloadLocked
                : handleDownloadXml
            }
            onDownloadXmlSibelius={
              canDownloadXml
                ? lockDownloadsForGuest
                  ? handleGuestDownloadLocked
                  : handleDownloadXmlSibelius
                : undefined
            }
            onDownloadXmlPlain={
              canDownloadXml && !isSample
                ? lockDownloadsForGuest
                  ? handleGuestDownloadLocked
                  : handleDownloadXmlPlain
                : undefined
            }
            isDownloadingXmlSibelius={isDownloadingXmlSibelius}
            isDownloadingXml={isDownloadingXml}
            isDownloadingXmlPlain={isDownloadingXmlPlain}
            onReset={handleEditReset}
            canReset={editUndoCount > 0}
            onUndo={handleEditUndo}
            canUndo={editUndoCount > 0}
            onDone={handleEditDone}
            isSaving={loading}
            onShowTutorial={() => {
              setTourOpen(true);
              trackButtonClick('PdfProjectPage', 'Open Tutorial', sampleProps);
            }}
            feedback={feedback}
          />
        )}

        {/* Floating right-side toggles for the score overlays.
            Remains visible during edit mode — the pencil pill in
            the column doubles as the edit-mode toggle (pressed
            state mirrors `isEditMode`), so hiding the column
            would also hide the only way to flip it back off via
            this UI. The centered Reset / Undo / Done strip in the
            top toolbar still appears in parallel. Rendered here
            (above the centred column) because it's position-fixed
            and doesn't depend on layout flow. */}
        {isReady && (
          <ScoreSideToggles
            showAccidental={showAccidental}
            showAltFingerings={showAltFingerings}
            showPitch={showPitch}
            showFingerings={showFingerings}
            hasNoteheadData={hasNoteheadData}
            onToggleAccidental={handleToggleAccidental}
            onToggleAlt={handleToggleAltFingerings}
            onTogglePitch={handleTogglePitch}
            onToggleFingerings={handleToggleFingerings}
            onTogglePencil={handleTogglePencil}
            isEditMode={isEditMode}
          />
        )}

        {/* Mobile (< md) bottom bar: score toggles on the left, the
            Font Size / Download / Help actions on the right. Mirrors
            the values passed to the top ProjectToolbar + the desktop
            side toggles above. */}
        {isReady && (
          <ProjectMobileBottomBar
            mode={isEditMode ? 'edit' : 'normal'}
            showAccidental={showAccidental}
            showAltFingerings={showAltFingerings}
            showPitch={showPitch}
            showFingerings={showFingerings}
            hasNoteheadData={hasNoteheadData}
            onToggleAccidental={handleToggleAccidental}
            onToggleAlt={handleToggleAltFingerings}
            onTogglePitch={handleTogglePitch}
            onToggleFingerings={handleToggleFingerings}
            onTogglePencil={handleTogglePencil}
            isEditMode={isEditMode}
            onReset={handleEditReset}
            canReset={editUndoCount > 0}
            onUndo={handleEditUndo}
            canUndo={editUndoCount > 0}
            onDone={handleEditDone}
            isSaving={loading}
            fontPopoverOpen={fontPopoverOpen}
            onFontPopoverOpenChange={setFontPopoverOpen}
            fontSize={fingeringSize}
            onFontSizeChange={handleFontSizeChange}
            downloadPopoverOpen={downloadPopoverOpen}
            onDownloadPopoverOpenChange={setDownloadPopoverOpen}
            onDownloadPdf={
              lockDownloadsForGuest
                ? handleGuestDownloadLocked
                : handleDownloadPdf
            }
            isDownloadingPdf={isExportingPdf}
            xmlAvailability={canDownloadXml ? 'ready' : 'unavailable'}
            onDownloadXml={
              lockDownloadsForGuest
                ? handleGuestDownloadLocked
                : handleDownloadXml
            }
            isDownloadingXml={isDownloadingXml}
            onDownloadXmlSibelius={
              canDownloadXml
                ? lockDownloadsForGuest
                  ? handleGuestDownloadLocked
                  : handleDownloadXmlSibelius
                : undefined
            }
            isDownloadingXmlSibelius={isDownloadingXmlSibelius}
            onDownloadXmlPlain={
              canDownloadXml && !isSample
                ? lockDownloadsForGuest
                  ? handleGuestDownloadLocked
                  : handleDownloadXmlPlain
                : undefined
            }
            isDownloadingXmlPlain={isDownloadingXmlPlain}
            onShowTutorial={() => {
              setTourOpen(true);
              trackButtonClick('PdfProjectPage', 'Open Tutorial', sampleProps);
            }}
            feedback={feedback}
          />
        )}

        {currentStatus !== 'GENERATION_COMPLETED' && (
          <GenerationWaitingView
            status={currentStatus}
            phase={poll.generationPhase}
            awaitingHandSelection={awaitingHandSelection}
            isPolling={isPolling}
            statusMessage={statusMessage}
            hasError={Boolean(error)}
            answeredWaitingSurveyTypes={answeredWaitingSurveyTypes}
            backTo={
              isSample
                ? (sample?.backTo ?? '/')
                : isGuest
                  ? guestHomeTo
                  : '/workspace'
            }
            backLabel={isSample || isGuest ? 'Home' : 'My Project'}
            onBack={() => {
              if (isSample) {
                trackButtonClick(
                  'PdfProjectPage',
                  'Back to Home (Sample)',
                  sampleProps
                );
                return;
              }
              trackButtonClick(
                'PdfProjectPage',
                isGuest ? 'Back to Home' : 'Back to Workspace'
              );
            }}
          />
        )}

        {/* Hidden rather than unmounted while waiting: the PNG effect and
            the edit listeners attach to `pngContainerRef`. */}
        <div
          className={`
            flex w-full min-w-0 flex-col items-center
            ${currentStatus !== 'GENERATION_COMPLETED' ? 'hidden' : ''}
          `}
        >
          <ScoreViewport
            className={`w-full max-w-7xl bg-white text-gray-600`}
            footer={scoreFooter}
          >
            {/* PNG Images Display */}
            <div className="my-4 min-h-64 w-11/12 min-w-11/12">
              {/* No page translation: the overlays redraw their digits
                  and pitch letters on every toggle. */}
              <div
                ref={pngContainerRef}
                translate="no"
                className="w-full"
              ></div>
            </div>
          </ScoreViewport>
        </div>
      </div>

      <ProjectTutorial open={tourOpen} onClose={handleTourClose} />

      {/* No global footer on the project page — the editing surface
          (toolbar + toggles) is the whole chrome at every screen size. */}
      <DialogComponent />
      {awaitingHandSelection && projectId && (
        <SinglePartHandDialog
          projectId={projectId}
          kind={kindFromStorageFileName(storageFileName)}
          reportedInstrument={reportedInstrument}
          // The next poll picks up PENDING_GENERATION and the page switches
          // to the generating view on its own; nothing to do here.
          onHandSelected={() => {}}
          onInstrumentReported={() => navigate('/workspace')}
        />
      )}
    </div>
  );
};

export const PdfProjectPageWithMsal = () => {
  return (
    <GuestProjectGuard>
      <ProjectPage />
    </GuestProjectGuard>
  );
};

/** Same page, static snapshot, no `GuestProjectGuard`. */
export const SampleProjectView: React.FC<{
  sample: ProjectPageSampleSource;
}> = ({ sample }) => <ProjectPage sample={sample} />;
